import { useCallback, useMemo, useRef } from "react";

import {
  GET_ANALYSIS_TEMPLATES_DEFAULT_PARAMS,
  useGetInfiniteAnalysisTemplates
} from "@/api/analysis/useAnalysisAPI";
import LoadingWrapper from "@/components/ui/LoadingWrapper";
import type { GetAnalysisTemplate, Template } from "@/types/template";
import { getPatternColorNameByIndex } from "@/utils/string";

import { TemplateCard } from "./TemplateCard";

interface TemplateSelectionModalProps {
  onSelect: (template: Template) => void;
}

export function TemplateSelectionModal({
  onSelect
}: Readonly<TemplateSelectionModalProps>) {
  const {
    data: analysisTemplates,
    fetchNextPage: fetchNextAnalysisTemplates,
    isLoading: isGettingAnalysisTemplates,
    isFetchingNextPage: isGettingAnalysisTemplatesNextPage,
    hasNextPage: hasNextAnalysisTemplatesPage
  } = useGetInfiniteAnalysisTemplates(GET_ANALYSIS_TEMPLATES_DEFAULT_PARAMS);

  const observerRef = useRef<IntersectionObserver | null>(null);

  const transformToTemplate = (data: GetAnalysisTemplate[]): Template[] => {
    return data.map((template, index) => ({
      ...template,
      color: getPatternColorNameByIndex(index),
      isActive: true
    }));
  };

  const templates: Template[] = useMemo(() => {
    if (!analysisTemplates?.pages) return [];

    return analysisTemplates.pages.flatMap((page) =>
      transformToTemplate(page.data ?? [])
    );
  }, [analysisTemplates]);

  const observeLastElement = useCallback(
    (node: HTMLDivElement | null) => {
      if (
        !node ||
        !hasNextAnalysisTemplatesPage ||
        isGettingAnalysisTemplatesNextPage
      )
        return;

      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            fetchNextAnalysisTemplates();
          }
        },
        { threshold: 1.0 }
      );

      observerRef.current.observe(node);
    },
    [
      fetchNextAnalysisTemplates,
      hasNextAnalysisTemplatesPage,
      isGettingAnalysisTemplatesNextPage
    ]
  );

  return (
    <div className="min-h-0 max-h-[600px] overflow-y-auto px-4 pb-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
        {templates.map((template, index) => {
          const isLast = index === templates.length - 1;
          return (
            <div
              key={template.id}
              ref={isLast ? observeLastElement : undefined}
            >
              <TemplateCard template={template} onSelect={onSelect} />
            </div>
          );
        })}
      </div>
      <div className="flex justify-center">
        <LoadingWrapper
          loadingClassName="w-24 h-24"
          isLoading={isGettingAnalysisTemplates}
        />
      </div>
    </div>
  );
}
